import { useCallback, useEffect, useState } from 'react';
import { useApi } from 'api/hooks/useApi';
import { LessonComment } from 'types';
import { getApiErrorMessage } from 'utils/apiError';

export function useLessonComments(lessonId: string, courseId: string | undefined) {
  const { request } = useApi();
  const [comments, setComments] = useState<LessonComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    if (!lessonId) {
      setComments([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await request<{ comments: LessonComment[] }>(
        `api/lesson-comments/by-lesson/${lessonId}?courseId=${courseId}`,
        { method: 'GET' }
      );
      const sorted = [...response.comments].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setComments(sorted);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao carregar comentários'));
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [lessonId, courseId, request]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const createComment = async (content: string): Promise<LessonComment> => {
    setSubmitting(true);
    setError(null);

    try {
      const response = await request<{ comment: LessonComment }>(`api/lesson-comments?courseId=${courseId}`, {
        method: 'POST',
        body: JSON.stringify({ lessonId, content, courseId }),
      });
      setComments((prev) => [response.comment, ...prev]);
      return response.comment;
    } catch (err) {
      const message = getApiErrorMessage(err, 'Erro ao criar comentário');
      setError(message);
      throw new Error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const editComment = async (commentId: string, content: string): Promise<LessonComment> => {
    setError(null);

    try {
      const response = await request<{ comment: LessonComment }>(`api/lesson-comments/${commentId}?courseId=${courseId}`, {
        method: 'PATCH',
        body: JSON.stringify({ content, courseId }),
      });
      setComments((prev) => prev.map((comment) => (comment.id === commentId ? response.comment : comment)));
      return response.comment;
    } catch (err) {
      const message = getApiErrorMessage(err, 'Erro ao editar comentário');
      setError(message);
      throw new Error(message);
    }
  };

  const deleteComment = async (commentId: string): Promise<void> => {
    setError(null);

    try {
      await request(`api/lesson-comments/${commentId}?courseId=${courseId}`, { method: 'DELETE' });
      setComments((prev) => prev.filter((comment) => comment.id !== commentId));
    } catch (err) {
      const message = getApiErrorMessage(err, 'Erro ao excluir comentário');
      setError(message);
      throw new Error(message);
    }
  };

  return {
    comments,
    loading,
    submitting,
    error,
    fetchComments,
    createComment,
    editComment,
    deleteComment,
  };
}
